import { Rnd } from 'react-rnd';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PDFElement } from './types';
import { cn } from '@/lib/utils';
import { hapticMedium } from '@/lib/despiaHaptics';

interface PDFElementRendererProps {
  element: PDFElement;
  isActive: boolean;
  isEditable: boolean;
  onSelect: (id: string) => void;
  onUpdate: (id: string, updates: Partial<PDFElement>) => void;
  onDelete: (id: string) => void;
  onTextChange?: (id: string, text: string) => void;
}

export function PDFElementRenderer({
  element,
  isActive,
  isEditable,
  onSelect,
  onUpdate,
  onDelete,
  onTextChange,
}: PDFElementRendererProps) {
  const handleStyles = {
    width: 12,
    height: 12,
    background: 'white',
    border: '2px solid hsl(var(--primary))',
    borderRadius: '2px',
  };

  const isImage = element.type === 'signature' || element.type === 'stamp';
  const isText = element.type === 'text' || element.type === 'date';

  return (
    <Rnd
      position={{ x: element.x, y: element.y }}
      size={{ width: element.width, height: element.height }}
      lockAspectRatio={isImage}
      bounds="parent"
      disableDragging={!isEditable}
      enableResizing={isEditable ? {
        top: false,
        right: isText,
        bottom: isText,
        left: false,
        topRight: isImage,
        bottomRight: true,
        bottomLeft: isImage,
        topLeft: isImage,
      } : false}
      resizeHandleStyles={isActive && isEditable ? {
        topLeft: { ...handleStyles, top: -6, left: -6 },
        topRight: { ...handleStyles, top: -6, right: -6 },
        bottomLeft: { ...handleStyles, bottom: -6, left: -6 },
        bottomRight: { ...handleStyles, bottom: -6, right: -6 },
        right: { ...handleStyles, right: -6, top: '50%', transform: 'translateY(-50%)' },
        bottom: { ...handleStyles, bottom: -6, left: '50%', transform: 'translateX(-50%)' },
      } : {}}
      onDragStart={() => {
        hapticMedium(); // Feedback tactile - "grab"
        onSelect(element.id);
      }}
      onDragStop={(e, d) => {
        onUpdate(element.id, { x: d.x, y: d.y });
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        onUpdate(element.id, {
          width: parseInt(ref.style.width),
          height: parseInt(ref.style.height),
          x: position.x,
          y: position.y,
        });
      }}
      onClick={() => onSelect(element.id)}
      className={cn(
        'transition-shadow',
        isEditable ? 'cursor-move' : 'cursor-default',
        isActive && isEditable && 'z-20'
      )}
      style={{ zIndex: isActive ? 20 : 10 }}
    >
      <div
        className={cn(
          'relative w-full h-full rounded transition-all',
          // Images: transparent background, border only when active
          isImage && 'flex items-center justify-center',
          isImage && isActive && isEditable && 'border-2 border-dashed border-primary bg-white/20',
          // Text: minimal style
          isText && 'flex items-center',
          // Active state shadow
          isActive && isEditable && 'shadow-lg',
          // Hover feedback when not active
          !isActive && isEditable && 'hover:ring-2 hover:ring-primary/30'
        )}
      >
        {/* Content */}
        {isImage && (
          <img
            src={element.content}
            alt={element.type}
            className="w-full h-full object-contain pointer-events-none select-none"
            draggable={false}
            style={{ display: 'block' }}
          />
        )}

        {isText && (
          <div
            contentEditable={isEditable && isActive && element.type === 'text'}
            suppressContentEditableWarning
            onBlur={(e) => {
              if (onTextChange && element.type === 'text') {
                onTextChange(element.id, e.currentTarget.textContent || '');
              }
            }}
            className={cn(
              'w-full outline-none',
              element.type === 'text' && isEditable && isActive && 'cursor-text'
            )}
            style={{
              fontFamily: element.style?.fontFamily || 'sans-serif',
              fontSize: `${element.style?.fontSize || 16}px`,
              color: element.style?.color || '#1a1a2e',
              fontWeight: element.style?.isBold ? 'bold' : 'normal',
              fontStyle: element.style?.isItalic ? 'italic' : 'normal',
            }}
          >
            {element.content}
          </div>
        )}

        {/* Delete button */}
        {isActive && isEditable && (
          <Button
            variant="destructive"
            size="icon"
            className="absolute -top-3 -right-3 h-6 w-6 rounded-full shadow-md"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(element.id);
            }}
          >
            <X className="w-3 h-3" />
          </Button>
        )}
      </div>
    </Rnd>
  );
}
